import * as React from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';

const progressTrackVariants = cva('relative w-full overflow-hidden bg-stone', {
  variants: {
    size: {
      xs: 'h-1 rounded-control',
      sm: 'h-2 rounded-control',
      md: 'h-3 rounded-control',
      lg: 'h-4 rounded-control',
      xl: 'h-6 rounded-control',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const progressIndicatorVariants = cva('h-full w-full flex-1 transition-transform duration-500 ease-out', {
  variants: {
    variant: {
      default: 'bg-ink',
      success: 'bg-state-nominal',
      warning: 'bg-state-warning',
      danger: 'bg-state-critical',
      info: 'bg-graphite',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface ProgressProps
  extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>,
    VariantProps<typeof progressTrackVariants>,
    VariantProps<typeof progressIndicatorVariants> {
  showValue?: boolean;
  animated?: boolean;
  label?: string;
  striped?: boolean;
}

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(
  (
    {
      className,
      value = 0,
      max = 100,
      variant = 'default',
      size = 'md',
      showValue = false,
      animated = false,
      label,
      striped = false,
      ...props
    },
    ref
  ) => {
    const numericValue = Number(value ?? 0);
    const numericMax = Number(max ?? 100) || 100;
    const percentage = Math.min(Math.max((numericValue / numericMax) * 100, 0), 100);

    return (
      <div className={cn('w-full', className)}>
        {label && (
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-ui font-medium text-content-secondary">{label}</span>
            {showValue && (
              <span className="font-ui text-sm font-580 tabular-nums text-content-primary">
                {Math.round(percentage)}%
              </span>
            )}
          </div>
        )}

        <ProgressPrimitive.Root
          ref={ref}
          value={numericValue}
          max={numericMax}
          className={cn(progressTrackVariants({ size }))}
          aria-label={label || `Progress: ${Math.round(percentage)}%`}
          {...props}
        >
          <ProgressPrimitive.Indicator
            className={cn(
              progressIndicatorVariants({ variant }),
              animated && 'animate-status-breathe',
              striped && 'opacity-90'
            )}
            style={{ transform: `translateX(-${100 - percentage}%)` }}
          />
        </ProgressPrimitive.Root>

        {showValue && !label && (
          <div className="mt-2 text-center">
            <span className="text-xs font-medium text-content-muted">
              {Math.round(percentage)}% Complete
            </span>
          </div>
        )}
      </div>
    );
  }
);

Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
